import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Money, Spinner, KpiCard, StatusBadge, EmptyState,
  CHART_COLORS, ChartTooltip, tooltipChrome } from "@/components/common";
import { Card } from "@/components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend, AreaChart, Area,
} from "recharts";
import { CalendarClock, TrendingUp } from "lucide-react";

export default function Dashboard() {
  const { user, reportingCcy } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/dashboard?reporting_ccy=${reportingCcy}`).then((r) => setData(r.data));
  }, [reportingCcy]);

  if (user?.role === "customer") return <Navigate to="/portfolio" replace />;
  if (!data) return <Spinner label="Loading overview…" />;
  const k = data.kpis;
  const onboardingData = Object.entries(data.onboarding_by_status || {}).map(([name, value]) => ({ name: name.replace(/_/g, " "), value }));
  const divisionData = Object.entries(data.customers_by_division || {}).map(([name, value]) => ({ name, value }));
  const aumSeries = (data.aum_series || []).map((p) => ({ date: p.date.slice(5), value: p.value_reporting }));

  return (
    <div data-testid="staff-dashboard-screen" className="fade-up">
      <PageHeader overline="Command Centre" title="Overview" wm="skyline"
        subtitle="Firm-wide wealth, onboarding and compliance at a glance."
        testid="dashboard-header"
        actions={
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs text-muted-foreground" data-testid="data-updated-chip">
            <CalendarClock className="h-3.5 w-3.5 text-primary" /> Data updated {data.valuation_date} · T-1 (mock)
          </span>
        } />

      {/* KPI grid */}
      <div className="grid grid-cols-1 min-[430px]:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 mb-5">
        <KpiCard label={`Total AUM (${reportingCcy})`} testid="kpi-card-total-aum"
          sub="Excludes insurance cover">
          <Money value={k.aum} ccy={reportingCcy} />
          {aumSeries.length > 1 && (
            <div className="mt-2 -mb-1 h-10" data-testid="kpi-aum-sparkline">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={aumSeries} margin={{ top: 2, bottom: 0, left: 0, right: 0 }}>
                  <defs>
                    <linearGradient id="aumFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#064E3B" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#064E3B" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Tooltip content={<ChartTooltip ccy={reportingCcy} />} {...tooltipChrome} />
                  <Area type="monotone" dataKey="value" stroke="#064E3B" strokeWidth={2} fill="url(#aumFill)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </KpiCard>
        <KpiCard label="Active Customers" testid="kpi-card-customers" sub="Across all divisions">{k.customers}</KpiCard>
        <KpiCard label="Pending Onboarding" testid="kpi-card-pending-onboarding" sub="Draft + info requested">{k.pending_onboarding}</KpiCard>
        <KpiCard label="Compliance Queue" testid="kpi-card-compliance" sub="In review + escalated">{k.compliance_queue}</KpiCard>
      </div>
      <div className="grid grid-cols-1 min-[430px]:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 mb-6">
        <KpiCard label="Open Leads" testid="kpi-card-leads">{k.open_leads}</KpiCard>
        <KpiCard label="Open Service Requests" testid="kpi-card-srs">{k.open_srs}</KpiCard>
        <KpiCard label="Recon Exceptions" testid="kpi-card-recon">{k.recon_exceptions}</KpiCard>
        <KpiCard label="Reporting Currency" testid="kpi-card-ccy"><span className="text-primary">{reportingCcy}</span></KpiCard>
      </div>

      {/* Charts row */}
      <div className="grid lg:grid-cols-3 gap-5 mb-6">
        <Card className="p-6 card-elev lg:col-span-2" data-testid="onboarding-pipeline-chart">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Onboarding by Status</h3>
            <span className="text-xs text-muted-foreground">Applications in pipeline</span>
          </div>
          {onboardingData.length === 0 ? <EmptyState title="No onboarding applications yet" /> : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={onboardingData} margin={{ left: -10 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-12} textAnchor="end" height={54} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip content={<ChartTooltip money={false} />} cursor={{ fill: "rgba(6,78,59,0.05)" }} {...tooltipChrome} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {onboardingData.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card className="p-6 card-elev" data-testid="customers-division-chart">
          <h3 className="font-semibold mb-4">Customers by Division</h3>
          {divisionData.length === 0 ? <EmptyState title="No customers yet" /> : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={divisionData} dataKey="value" nameKey="name" cx="50%" cy="45%" innerRadius={52} outerRadius={82} paddingAngle={3}>
                  {divisionData.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip content={<ChartTooltip money={false} />} {...tooltipChrome} />
                <Legend wrapperStyle={{ fontSize: "12px", paddingTop: "8px" }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* Recent activity */}
      <Card className="p-6 card-elev" data-testid="dashboard-recent-activity-table">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="h-4 w-4 text-gold" />
          <h3 className="font-semibold">Recent Activity</h3>
          <span className="text-xs text-muted-foreground ml-auto">Latest platform events</span>
        </div>
        {(!data.recent_activity || data.recent_activity.length === 0) ? (
          <EmptyState title="No recent activity" hint="Platform events will appear here." />
        ) : (
          <div className="divide-y divide-border/60">
            {data.recent_activity.map((a, i) => (
              <div key={i} className="flex items-center gap-3 py-3" data-testid="recent-activity-row">
                <StatusBadge status={a.action} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium capitalize truncate">{(a.entity || "").replace(/_/g, " ")} · {(a.action || "").replace(/_/g, " ")}</div>
                  {a.details && <div className="text-xs text-muted-foreground truncate">{a.details}</div>}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs text-muted-foreground">{a.by}</div>
                  <div className="text-[11px] font-num text-muted-foreground/70">{(a.ts || "").slice(0, 10)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
